import { Button } from "react-bootstrap";
import { configsService } from "../../Services/configs.service";
import { vehicleService } from "../../Services/vehicle.service";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { vehicleGroupService } from "../../Services/vehicle.group.service";
import { ToastComponent } from "../Components/ToastComponent";
import { useTranslation } from "react-i18next";

export const LinkedVehicles = () => {
    const { t } = useTranslation();
    const [groupsConfigs, setGroupsConfigs] = useState([]); // dados originais vindos da API
    const [filteredGroupsConfigs, setFilteredGroupsConfigs] = useState([]); // dados mostrados/alterados localmente
    const [dataGroup, setDataGroup] = useState([]);
    const [filtersLoading, setFiltersLoading] = useState(false);
    const [vehicles, setVehicles] = useState([]);
    const [selectedGroupName, setSelectedGroupName] = useState("");
    const [selectedPlate, setSelectedPlate] = useState("");

    // mapa vehicleId -> newGroupId para rastrear alterações
    const [changedMap, setChangedMap] = useState({});

    const [toastParams, setToastParams] = useState(null);

    const dispatch = useDispatch();
    dispatch({ type: "title", value: t("LINKED_VEHICLES.TITLE") });

    // utilitário para extrair id do item (resiliente a campos diferentes)
    const getVehicleId = (item) =>
        item?.vehicleId ?? item?.id ?? item?.groupVehicleId ?? null;

    const fetchVehicleGroups = async (item) => {
        try {
          setFiltersLoading(true);
          const groups = await vehicleService.fetchGroups();
          const vehicles = await vehicleService.fetchVehicles(item.vehicleGroupId);
          const groupsConfigsRaw = await configsService.fetchGroupsConfigs(item.vehicleGroupId);
      
          const safeGroups = groups ?? [];
          const safeVehicles = vehicles ?? [];
          const safeGroupsConfigs = groupsConfigsRaw ?? [];
      
          setDataGroup(safeGroups);
          setVehicles(safeVehicles);
          setGroupsConfigs(safeGroupsConfigs);
          setFilteredGroupsConfigs(safeGroupsConfigs.map(g => ({ ...g })));
          setChangedMap({});
        } catch (error) {
          console.log(error);
        } finally {
          setFiltersLoading(false);
        }
      };
      
    // quando o usuário altera o select, atualiza a tabela local e marca alteração
    const handleLocalChange = (index, newValue) => {
        setFilteredGroupsConfigs((prev) =>
            prev.map((v, i) => (i === index ? { ...v, groupId: newValue } : v))
        );

        // marca alteração no mapa usando o vehicleId real
        const item = filteredGroupsConfigs[index] || groupsConfigs[index];
        const vid = getVehicleId(item);
        if (!vid) return;

        setChangedMap((prev) => {
            // se newValue for igual ao valor original, remove do mapa (desfaz alteração)
            const original = (groupsConfigs.find(g => getVehicleId(g) === vid) || {}).groupId;
            if ((newValue ?? "") === (original ?? "")) {
                const copy = { ...prev };
                delete copy[vid];
                return copy;
            }

            return {
                ...prev,
                [vid]: newValue,
            };
        });
    };

    // função de salvar todas alterações (chama API para cada item alterado)
    const onSaveAll = async () => {
        const entries = Object.entries(changedMap);
        if (entries.length === 0) {
            setToastParams(JSON.stringify({
                show: true,
                title: t("LINKED_VEHICLES.NOTHING_TO_SAVE"),
                body: t("LINKED_VEHICLES.NO_CHANGES_TO_SAVE"),
                color: "Info"
            }));
            return;
        }

        let successCount = 0;
        let failCount = 0;
        const errors = [];

        // envia em série ou em paralelo – aqui uso Promise.all para paralelizar
        const promises = entries.map(async ([vehicleId, groupId]) => {
            try {
                const payload = { groupId, vehicleId };
                const resp = await vehicleGroupService.sendGroups(payload);
                // ajuste caso sua API retorne estrutura diferente
                if (resp) {
                    successCount += 1;
                    return { vehicleId, ok: true };
                } else {
                    failCount += 1;
                    errors.push({ vehicleId, msg: t("LINKED_VEHICLES.NO_RESPONSE") });
                    return { vehicleId, ok: false };
                }
            } catch (err) {
                failCount += 1;
                errors.push({ vehicleId, msg: err?.message || t("COMMON.ERROR") });
                return { vehicleId, ok: false };
            }
        });

        await Promise.all(promises);

        // Após salvar, atualiza groupsConfigs (dados originais) para refletir novas associações
        const updatedOriginals = groupsConfigs.map((orig) => {
            const vid = getVehicleId(orig);
            if (changedMap[vid] !== undefined) {
                return { ...orig, groupId: changedMap[vid] };
            }
            return orig;
        });

        setGroupsConfigs(updatedOriginals);

        // Também atualiza a lista mostrada (poderia re-fetchar do servidor se preferir)
        setFilteredGroupsConfigs((prev) =>
            prev.map((item) => {
                const vid = getVehicleId(item);
                if (changedMap[vid] !== undefined) {
                    return { ...item, groupId: changedMap[vid] };
                }
                return item;
            })
        );

        // limpa mapa de alterações
        setChangedMap({});

        // mostra toast resumo
        setToastParams(JSON.stringify({
            show: true,
            title: t("LINKED_VEHICLES.SAVE_CHANGES"),
            body: `${t("COMMON.SUCCESS")}: ${successCount}. ${t("LINKED_VEHICLES.FAILURES")}: ${failCount}.`,
            color: failCount === 0 ? "Success" : "Warning",
            details: errors.length ? errors : undefined
        }));
    };

    // chamada inicial
    useEffect(() => {
        fetchVehicleGroups({ vehicleGroupId: 0 });
    }, []);

    useEffect(() => {
        let filtered = [...groupsConfigs];
      
        if (selectedGroupName) {
          filtered = filtered.filter((v) =>
            v.groupName?.toLowerCase().includes(selectedGroupName.toLowerCase())
          );
        }
      
        if (selectedPlate) {
          filtered = filtered.filter((v) =>
            v.plate?.toLowerCase().includes(selectedPlate.toLowerCase())
          );
        }
      
        setFilteredGroupsConfigs(filtered);
      }, [selectedGroupName, selectedPlate, groupsConfigs]);
      

    return (
        <div style={{paddingLeft: 15}}>
            <ToastComponent params={toastParams} closeToast={() => setToastParams(JSON.stringify({ show: false }))} />

            {/* Filtros */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "20px",
                }}
            >
                {/* Filtro de grupos */}
                <div className="mb-1 col-md-4">
                    <label className="form-label fw-bold">{t("LINKED_VEHICLES.FILTER_BY_GROUP")}:</label>
                    <select
                        className="filter-select form-select-sm"
                        value={selectedGroupName}
                        onChange={(e) => setSelectedGroupName(e.target.value)}
                    >
                        <option value="">{t("LINKED_VEHICLES.ALL_GROUPS")}</option>
                        {dataGroup &&
                            dataGroup.map((group) => (
                                <option key={group.vehicleGroupId} value={group.vehicleGroupName}>
                                    {group.vehicleGroupName}
                                </option>
                            ))}
                    </select>
                </div>

                {/* Filtro de veículos */}
                <div className="mb-1 col-md-4">
                    <label className="form-label fw-bold">{t("LINKED_VEHICLES.FILTER_BY_PLATE")}:</label>
                    <select
                        className="filter-select form-select-sm"
                        value={selectedPlate}
                        onChange={(e) => setSelectedPlate(e.target.value)}
                    >
                        <option value="">{t("LINKED_VEHICLES.ALL_PLATES")}</option>
                        {vehicles &&
                            vehicles.map((vehicle) => (
                                <option key={vehicle.vehicleId} value={vehicle.plate}>
                                    {vehicle.plate}
                                </option>
                            ))}
                    </select>
                </div>
            </div>


            {/* Tabela */}
            <div style={{ marginTop: "20px" }}>
                <div className="table-responsive">
                    <table className="table table-striped table-bordered align-middle">
                        <thead className="table-light">
                            <tr>
                                <th>{t("LINKED_VEHICLES.PLATE")}</th>
                                <th>{t("LINKED_VEHICLES.CURRENT_GROUP")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredGroupsConfigs && filteredGroupsConfigs.length > 0 ? (
                                filteredGroupsConfigs.map((vehicle, index) => (
                                    <tr key={index}>
                                        <td>{vehicle.plate}</td>
                                        <td>
                                            <select
                                                className="filter-select form-select-sm"
                                                value={vehicle.groupId ?? ""}
                                                onChange={(e) => handleLocalChange(index, e.target.value)}
                                            >
                                                <option value="">{t("LINKED_VEHICLES.SELECT_A_GROUP")}</option>
                                                {dataGroup &&
                                                    dataGroup.map((g) => (
                                                        <option key={g.vehicleGroupId} value={g.vehicleGroupId}>
                                                            {g.vehicleGroupName}
                                                        </option>
                                                    ))}
                                            </select>
                                            {/* indica visualmente que este item foi alterado */}
                                            {(() => {
                                                const vid = getVehicleId(vehicle);
                                                return changedMap[vid] ? (
                                                    <span style={{ marginLeft: 8, fontSize: 12, color: "#0d6efd" }}>
                                                        ({t("LINKED_VEHICLES.CHANGED")})
                                                    </span>
                                                ) : null;
                                            })()}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="2" className="text-center">
                                        {t("LINKED_VEHICLES.NO_VEHICLES_FOUND")}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                    <Button
                        variant="primary"
                        onClick={onSaveAll}
                        disabled={Object.keys(changedMap).length === 0}
                    >
                        {t("LINKED_VEHICLES.SAVE_CHANGES")} ({Object.keys(changedMap).length})
                    </Button>

                    <Button
                        variant="secondary"
                        onClick={() => {
                            // desfaz alterações: restaura o estado local para o original
                            setFilteredGroupsConfigs(groupsConfigs?.map(g => ({ ...g })));
                            setChangedMap({});
                        }}
                        disabled={Object.keys(changedMap).length === 0}
                    >
                        {t("LINKED_VEHICLES.CANCEL_CHANGES")}
                    </Button>
                </div>
            </div>
        </div>
    );
};